import { useTenderActionError } from "../../../../../hooks/useTenderActionError";
import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { Aviation } from "../../../../../services";
import CustomInputCurrency from "../../../../custom/custom-input-currency/CustomInputCurrency";
import CustomButton from "../../../../custom/custom-button/CustomButton";
import CloseIconNew from "../../../../shared/icons/CloseIconNew";
import HintIcon from "../../../../shared/icons/HintIcon";
import { CURRENCIES } from "../../../../../mock";
import style from "./AviationExpeditorUpdateBidModal.module.scss";

const AviationExpeditorUpdateBidModal = ({ onRefreshOrder, isOpen, onClose, bid, order, onSuccess }) => {
  const { t } = useTranslation();
  const { requestErrorDisplay } = useTenderActionError(order, { onRefreshOrder });
  const [price, setPrice] = useState("");
  const [currency, setCurrency] = useState("EUR");
  const [errorText, setErrorText] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const lockCurrency = Boolean(order?.acceptedCurrency) || order?.assignmentMethod === "Auction";
  const isAuction = order?.assignmentMethod === "Auction";
  const reductionStep = Number(order?.bidReductionStep);
  const hasReductionStep = isAuction && Number.isFinite(reductionStep) && reductionStep > 0;
  const currentMinPrice = Number(bid?.minPrice);
  const hasCurrentMinPrice = bid?.minPrice != null && Number.isFinite(currentMinPrice) &&
    currentMinPrice > 0 && !bid?.isMultiCurrency && currency === bid?.currency;
  const currencySymbol = CURRENCIES[currency]?.symbol || currency;
  const stepPrice = hasReductionStep ? `${reductionStep.toLocaleString("ru-RU")} ${currencySymbol}` : "";

  useEffect(() => {
    if (!isOpen) return;
    setPrice("");
    setCurrency(order?.acceptedCurrency || bid?.currency || order?.currency || "EUR");
    setErrorText("");
  }, [bid?.currency, bid?.id, isOpen, order?.acceptedCurrency, order?.currency]);

  if (!isOpen) return null;

  const handleClose = () => {
    setPrice("");
    setErrorText("");
    onClose?.();
  };

  const handleUpdate = async () => {
    const roundedPrice = Math.round(Number(price));
    if (!Number.isFinite(roundedPrice) || roundedPrice <= 0) {
      setErrorText(t("orderInfoExpeditor.bid.correctBidRequired"));
      return;
    }
    if (roundedPrice > 2000000000) {
      setErrorText(t("orderInfoExpeditor.bid.bidTooLarge"));
      return;
    }

    if (isAuction && hasCurrentMinPrice && roundedPrice >= currentMinPrice) {
      setErrorText(t("complexTender.updateBid.belowBestBidRequired"));
      return;
    }
    if (hasReductionStep && hasCurrentMinPrice && roundedPrice > currentMinPrice - reductionStep) {
      setErrorText(t("orderInfoExpeditor.bid.reductionStepError", { step: stepPrice }));
      return;
    }

    setIsLoading(true);
    try {
      await Aviation.updateSuggestedPrice(bid.id, {
        orderId: bid.orderId,
        price: roundedPrice,
        currency: order?.acceptedCurrency || currency,
        comments: bid.comments,
        weight: bid.weight,
        volume: bid.volume,
        plannedDays: bid.plannedDays,
        isExtraCosts: Boolean(bid.isExtraCosts),
      });
      toast.success(t("orderInfoExpeditor.bid.bidUpdated"));
      onSuccess?.();
    } catch (error) {
      console.error(error);
      await requestErrorDisplay(error, t, "orderInfoExpeditor.bid.updateBidError");
    } finally {
      setIsLoading(false);
    }
  };

  return ReactDOM.createPortal(
    <div className={style.overlay} onClick={handleClose} data-portal-modal="true">
      <div className={style.modal} onClick={(event) => event.stopPropagation()}>
        <div className={style.header}>
          <span className={style.title}>{t("orderInfoExpeditor.bid.changeBid")}</span>
          <button
            type="button"
            className={style.close_btn}
            onClick={handleClose}
            aria-label={t("orderInfoExpeditor.assignDriverModal.close")}
          >
            <CloseIconNew width="24" height="24" fill="#212121" />
          </button>
        </div>
        <div className={style.body}>
          <CustomInputCurrency
            value={price}
            isBgGray
            inputType="number"
            labelText={t("orderInfoExpeditor.bid.newBidLabel")}
            placeholder={t("orderInfoExpeditor.bid.yourBidPlaceholder")}
            errorText={errorText}
            currency={currency}
            lockCurrency={lockCurrency}
            onChangeValue={(value) => {
              setPrice(value);
              setErrorText("");
            }}
            onChangeCurrency={(value) => {
              setCurrency(value);
              setErrorText("");
            }}
          />
          {hasReductionStep && (
            <div className={style.reduction_step_hint}>
              <HintIcon aria-hidden="true" />
              <span>{t("orderInfoExpeditor.bid.reductionStepHint", { step: stepPrice })}</span>
            </div>
          )}
        </div>

        <div className={style.footer}>
          <div className={style.footer_btn}>
            <CustomButton
              text={t("common.cancel")}
              isNavy
              isWhite
              onClick={handleClose}
              disabled={isLoading}
              style={{ width: "100%" }}
            />
          </div>
          <div className={style.footer_btn}>
            <CustomButton
              text={t("orderInfoExpeditor.bid.update")}
              isNavy
              onClick={handleUpdate}
              isLoading={isLoading}
              disabled={isLoading}
              style={{ width: "100%" }}
            />
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default AviationExpeditorUpdateBidModal;
